import React, { useMemo, useState } from 'react';
import { DModal, DPageLoader } from '../../../../../dum';
import { AccountIcon } from '../../../../icons';
import { AccountForm } from './account-form';
import { useAccountDialogQuery } from '../../../../../../services';

interface ISAccountFormDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    accountId?: number | null;
    disabled?: boolean;
}

export const SUpsertAccountDialog = (props: ISAccountFormDialogProps) => {
    const { open, setOpen, accountId } = props;
    const [isModalClosing, setIsModalClosing] = useState(false);

    const { data, loading } = useAccountDialogQuery({
        skip: !accountId || !open,
        variables: {
            AccountId: accountId!,
        },
    });

    const account = useMemo(() => {
        return accountId && data && data.getAccount && open
            ? data.getAccount
            : undefined;
    }, [accountId, data, open]);

    const accountProducts = useMemo(() => {
        return (data?.getAccount?.account_products || [])
            .filter((accountProduct) => !!accountProduct.product)
            .map((accountProduct) => ({
                id: accountProduct.id,
                product: accountProduct.product,
                kilo_price: accountProduct.kilo_price,
                group_price: accountProduct.group_price,
                group_weight: accountProduct.group_weight,
            }))
            // Sort the catalog first by product category name, then by product
            // name — the rows render in this order (new rows append to the end).
            .sort((a, b) => {
                const byCategory = (
                    a.product?.product_category?.name ?? ''
                ).localeCompare(b.product?.product_category?.name ?? '');
                if (byCategory !== 0) return byCategory;
                return (a.product?.compound_description ?? '').localeCompare(
                    b.product?.compound_description ?? '',
                );
            });
    }, [data]);

    const accountResources = useMemo(() => {
        return (data?.getAccount?.account_resources || [])
            .filter((accountResource) => !!accountResource.resource)
            .map((accountResource) => ({
                id: accountResource.id,
                resource: accountResource.resource,
                unit_price: accountResource.unit_price,
                notes: accountResource.notes,
            }));
    }, [data]);

    const resources = useMemo(() => {
        return data?.getResources || [];
    }, [data]);

    const products = useMemo(() => {
        const base = data?.getProducts || [];
        // getProducts excludes discontinued products, but an existing catalog
        // row may reference one that has since been discontinued. The product
        // autocomplete resolves its label from this options list, so a missing
        // option leaves that row's product field blank. Merge the catalog's own
        // products in (deduped by id) so those rows render. New discontinued
        // products still can't be added — only ones already in the catalog
        // appear, and uniqueness validation blocks re-adding them.
        const byId = new Map<number, (typeof base)[number]>();
        base.forEach((p) => byId.set(p.id, p));
        (data?.getAccount?.account_products || []).forEach((ap) => {
            if (ap.product && !byId.has(ap.product.id)) {
                byId.set(ap.product.id, {
                    id: ap.product.id,
                    compound_description: ap.product.compound_description,
                    current_group_weight: ap.product.current_group_weight,
                    product_category: ap.product.product_category,
                });
            }
        });
        return Array.from(byId.values());
    }, [data]);

    return (
        <DModal
            icon={<AccountIcon />}
            title={account?.name ?? (accountId ? 'Cuenta' : 'Crear cuenta')}
            open={open}
            setOpen={setOpen}
            onExited={() => {
                setIsModalClosing(false);
            }}
        >
            {loading ? (
                <DPageLoader />
            ) : (
                <AccountForm
                    disabled={isModalClosing}
                    account={account}
                    accountProducts={accountProducts}
                    accountResources={accountResources}
                    products={products}
                    resources={resources}
                    onSuccess={() => {
                        setIsModalClosing(true);
                        setOpen(false);
                    }}
                />
            )}
        </DModal>
    );
};
